/**
 * Sticky PDP buy bar.
 * Appears as the inline buy row scrolls above the sticky header.
 * Button outlines track that scroll: round pills melt into flush
 * half-width rectangles. They are not separate circular buttons.
 * prefers-reduced-motion shows the final rectangles with no melt.
 * Get Full Package and Add to cart click those same inline controls.
 */
(function () {
  const STICKY_BUY_MORPH_MIN_PX = 64;

  function stickyBuyShouldShow(rect, headerBottom) {
    if (!rect || typeof rect.bottom !== 'number' || typeof rect.height !== 'number') return false;
    if (rect.height <= 0) return false;
    const topLimit = Number.isFinite(headerBottom) ? headerBottom : 0;
    return rect.bottom <= topLimit + 0.5;
  }

  function stickyBuyMorphProgress(rect, headerBottom, options) {
    const settings = options || {};
    if (settings.reduceMotion === true) {
      return stickyBuyShouldShow(rect, headerBottom) ? 1 : 0;
    }
    if (!rect || typeof rect.bottom !== 'number' || typeof rect.height !== 'number' || !(rect.height > 0)) {
      return 0;
    }
    if (stickyBuyShouldShow(rect, headerBottom)) return 1;
    const topLimit = Number.isFinite(headerBottom) ? headerBottom : 0;
    let span = rect.height;
    if (Number.isFinite(settings.range) && settings.range > 0) span = settings.range;
    else if (span < STICKY_BUY_MORPH_MIN_PX) span = STICKY_BUY_MORPH_MIN_PX;
    const distance = rect.bottom - topLimit;
    if (distance >= span) return 0;
    const progress = (span - distance) / span;
    if (progress < 0) return 0;
    if (progress > 1) return 1;
    return progress;
  }

  function stickyHeaderCoverBottom(rects) {
    const list = Array.isArray(rects) ? rects : [];
    let bottom = 0;
    let changed = true;
    while (changed) {
      changed = false;
      for (let i = 0; i < list.length; i += 1) {
        const rect = list[i];
        if (!rect || typeof rect.top !== 'number' || typeof rect.bottom !== 'number') continue;
        if (rect.top <= bottom + 1 && rect.bottom > bottom) {
          bottom = rect.bottom;
          changed = true;
        }
      }
    }
    return bottom > 0 ? bottom : 0;
  }

  function triggerStickyBuyAction(action, sources) {
    const bag = sources || {};
    const source = action === 'package' ? bag.packageSource : bag.cartSource;
    if (!source || typeof source.click !== 'function') return false;
    if (source.disabled === true) return false;
    const aria = typeof source.getAttribute === 'function' ? source.getAttribute('aria-disabled') : source.ariaDisabled;
    if (aria === true || aria === 'true') return false;
    source.click();
    return true;
  }

  function stickyCartButtonState(source) {
    if (!source) return null;
    let label = '';
    if (typeof source.querySelector === 'function') {
      const span = source.querySelector('span');
      if (span && typeof span.textContent === 'string') label = span.textContent;
    } else if (typeof source.label === 'string') {
      label = source.label;
    }
    const aria = typeof source.getAttribute === 'function' ? source.getAttribute('aria-disabled') : source.ariaDisabled;
    const unavailable = source.disabled === true;
    const loading = source.classList && typeof source.classList.contains === 'function'
      ? source.classList.contains('loading')
      : source.loading === true;
    return {
      label: label.replace(/\s+/g, ' ').trim(),
      unavailable: unavailable,
      busy: unavailable || aria === true || aria === 'true',
      loading: loading,
    };
  }

  const api = {
    stickyBuyShouldShow: stickyBuyShouldShow,
    stickyBuyMorphProgress: stickyBuyMorphProgress,
    stickyHeaderCoverBottom: stickyHeaderCoverBottom,
    triggerStickyBuyAction: triggerStickyBuyAction,
    stickyCartButtonState: stickyCartButtonState,
  };

  if (typeof window !== 'undefined') window.StickyBuyBar = api;

  if (typeof customElements === 'undefined' || !customElements || customElements.get('sticky-buy-bar')) return;
  if (typeof HTMLElement === 'undefined') return;

  let missingBindLogged = false;

  function bindLayoutBatch(read, write) {
    const chrome = window.PageScrollChrome;
    if (chrome && typeof chrome.bind === 'function') return chrome.bind(read, write);
    if (!missingBindLogged) {
      missingBindLogged = true;
      console.error('PageScrollChrome.bind is missing; sticky buy bar will stay hidden');
    }
    return {
      schedule() {},
      detach() {},
    };
  }

  class StickyBuyBar extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.takeColorScheme();
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return;
      }
      document.querySelectorAll('sticky-buy-bar').forEach((el) => {
        if (el !== this) el.remove();
      });
      this.dataset.ready = 'true';
      this.mount();
    }

    disconnectedCallback() {
      if (this.dataset.ready !== 'true') return;
      this.removeAttribute('data-ready');
      this.teardown();
    }

    takeColorScheme() {
      const host = document.querySelector('product-info[id^="MainProduct-"]');
      if (!host) return;
      host.classList.forEach((name) => {
        if (name === 'gradient' || name.indexOf('color-') === 0) this.classList.add(name);
      });
    }

    mount() {
      this.shown = false;
      this.controlsLocked = null;
      this.reduceQuery = null;
      this.row = null;
      this.packageSource = null;
      this.cartSource = null;
      this.pending = null;
      this.packageButton = this.querySelector('[data-sticky-buy="package"]');
      this.cartButton = this.querySelector('[data-sticky-buy="cart"]');
      this.batch = bindLayoutBatch(() => this.readFrame(), () => this.writeFrame());
      this.onViewportChange = () => this.batch.schedule();
      this.boundClick = (event) => this.onClick(event);
      this.querySelectorAll('[data-sticky-buy]').forEach((button) => {
        button.addEventListener('click', this.boundClick);
      });
      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange);
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.onViewportChange);
        window.visualViewport.addEventListener('scroll', this.onViewportChange);
      }
      document.addEventListener('shopify:section:load', this.onViewportChange);
      if (window.matchMedia) {
        this.reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.onReduceMotion = () => this.schedule();
        if (this.reduceQuery.addEventListener) {
          this.reduceQuery.addEventListener('change', this.onReduceMotion);
        }
      }
      this.schedule();
    }

    teardown() {
      if (this.batch) this.batch.detach();
      this.batch = null;
      if (this.onViewportChange) {
        window.removeEventListener('scroll', this.onViewportChange);
        window.removeEventListener('resize', this.onViewportChange);
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', this.onViewportChange);
          window.visualViewport.removeEventListener('scroll', this.onViewportChange);
        }
        document.removeEventListener('shopify:section:load', this.onViewportChange);
      }
      if (this.reduceQuery && this.onReduceMotion && this.reduceQuery.removeEventListener) {
        this.reduceQuery.removeEventListener('change', this.onReduceMotion);
      }
      this.classList.remove('sticky-buy-bar--ready', 'sticky-buy-bar--visible', 'sticky-buy-bar--engaged', 'sticky-buy-bar--solo');
      this.setAttribute('hidden', '');
      this.setAttribute('inert', '');
      document.documentElement.classList.remove('sticky-buy-open');
      this.teardownSource();
    }

    schedule() {
      this.batch.schedule();
    }

    headerRects() {
      const nodes = document.querySelectorAll('.shopify-section-group-header-group, .section-header');
      return Array.from(nodes, (node) => node.getBoundingClientRect());
    }

    resolveRow() {
      if (this.row && !this.row.isConnected) {
        this.teardownSource();
        this.row = null;
        this.packageSource = null;
        this.cartSource = null;
      }
      const row = document.querySelector('product-info[id^="MainProduct-"] [data-sticky-buy-row]');
      if (row === this.row) return row;
      this.teardownSource();
      this.row = row;
      if (!row) {
        this.packageSource = null;
        this.cartSource = null;
        return null;
      }
      this.packageSource = row.querySelector('[data-ahri-package-open]');
      this.cartSource = row.querySelector('button[name="add"]');
      this.observeSource();
      return row;
    }

    observeSource() {
      this.sourceObserver = new MutationObserver(() => this.schedule());
      if (this.cartSource) {
        this.sourceObserver.observe(this.cartSource, {
          attributes: true,
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
      const form = this.row.closest('product-form');
      const error = form && form.querySelector('.product-form__error-message-wrapper');
      if (error) {
        this.errorObserver = new MutationObserver(() => {
          if (error.hasAttribute('hidden') || !this.row || !this.row.isConnected) return;
          const headerBottom = window.StickyBuyBar.stickyHeaderCoverBottom(this.headerRects());
          if (!window.StickyBuyBar.stickyBuyShouldShow(this.row.getBoundingClientRect(), headerBottom)) return;
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.row.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
        });
        this.errorObserver.observe(error, { attributes: true, attributeFilter: ['hidden'] });
      }
      if (typeof IntersectionObserver === 'function') {
        this.rowObserver = new IntersectionObserver(() => this.schedule());
        this.rowObserver.observe(this.row);
      }
    }

    teardownSource() {
      if (this.sourceObserver) this.sourceObserver.disconnect();
      if (this.errorObserver) this.errorObserver.disconnect();
      if (this.rowObserver) this.rowObserver.disconnect();
      this.sourceObserver = null;
      this.errorObserver = null;
      this.rowObserver = null;
    }

    readFrame() {
      const headerRects = this.headerRects();
      const headerBottom = window.StickyBuyBar.stickyHeaderCoverBottom(headerRects);
      const row = this.resolveRow();
      const rowRect = row && this.cartSource ? row.getBoundingClientRect() : null;
      this.pending = {
        headerBottom,
        row,
        packageOpen: !!this.packageSource,
        rowRect,
        state: row && this.cartSource ? window.StickyBuyBar.stickyCartButtonState(this.cartSource) : null,
        popup: row && this.cartSource ? this.cartSource.getAttribute('aria-haspopup') : null,
      };
    }

    writeFrame() {
      const next = this.pending;
      if (!next) return;
      this.pending = null;
      const topValue = `${Math.max(0, Math.round(next.headerBottom))}px`;
      if (this.stickyBuyTop !== topValue) {
        this.stickyBuyTop = topValue;
        document.documentElement.style.setProperty('--sticky-buy-top', topValue);
      }
      if (this.packageButton) this.packageButton.hidden = !next.packageOpen;
      this.classList.toggle('sticky-buy-bar--solo', !next.packageOpen);
      if (!next.row || !this.cartSource) {
        this.applyMorph(0, false);
        return;
      }
      if (next.state) this.applyCartState(next.state);
      if (next.popup) this.cartButton.setAttribute('aria-haspopup', next.popup);
      else this.cartButton.removeAttribute('aria-haspopup');
      const show = window.StickyBuyBar.stickyBuyShouldShow(next.rowRect, next.headerBottom);
      const progress = window.StickyBuyBar.stickyBuyMorphProgress(next.rowRect, next.headerBottom, {
        reduceMotion: !!(this.reduceQuery && this.reduceQuery.matches),
      });
      this.applyMorph(progress, show);
    }

    applyCartState(state) {
      const span = this.cartButton.querySelector('span');
      if (span && state.label) span.textContent = state.label;
      this.cartButton.classList.toggle('loading', state.loading);
      const spinner = this.cartButton.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !state.loading);
      this.cartButton.toggleAttribute('disabled', state.unavailable);
      if (state.busy) this.cartButton.setAttribute('aria-disabled', 'true');
      else this.cartButton.removeAttribute('aria-disabled');
    }

    applyMorph(progress, show) {
      const clamped = progress < 0 ? 0 : progress > 1 ? 1 : progress;
      const visible = clamped > 0;
      const morph = clamped.toFixed(4);
      if (this.stickyBuyMorph !== morph) {
        this.stickyBuyMorph = morph;
        this.style.setProperty('--sticky-buy-morph', morph);
      }
      this.classList.add('sticky-buy-bar--ready');
      this.classList.toggle('sticky-buy-bar--visible', visible);
      this.classList.toggle('sticky-buy-bar--engaged', show);
      if (this.shown && !show) this.releaseFocus();
      this.shown = show;
      const lockControls = !show;
      if (this.controlsLocked !== lockControls) {
        this.controlsLocked = lockControls;
        this.querySelectorAll('[data-sticky-buy]').forEach((button) => {
          if (lockControls) button.setAttribute('tabindex', '-1');
          else button.removeAttribute('tabindex');
        });
      }
      if (show) this.removeAttribute('inert');
      else this.setAttribute('inert', '');
      /* Resting progress 0 stays undisplayed. Entering the range reveals
         the half-width pills, then scroll melts their outlines square. */
      this.toggleAttribute('hidden', !visible);
      document.documentElement.classList.toggle('sticky-buy-open', show);
    }

    releaseFocus() {
      const active = document.activeElement;
      if (!active || !this.contains(active)) return;
      const action = active.getAttribute('data-sticky-buy');
      const target = action === 'package' ? this.packageSource : this.cartSource;
      if (target && target.disabled !== true && typeof target.focus === 'function') target.focus();
    }

    onClick(event) {
      if (this.hasAttribute('hidden')) return;
      const action = event.currentTarget.getAttribute('data-sticky-buy');
      const headerBottom = window.StickyBuyBar.stickyHeaderCoverBottom(this.headerRects());
      const rect = this.row && this.row.isConnected ? this.row.getBoundingClientRect() : null;
      if (!window.StickyBuyBar.stickyBuyShouldShow(rect, headerBottom)) return;
      window.StickyBuyBar.triggerStickyBuyAction(action, {
        packageSource: this.packageSource,
        cartSource: this.cartSource,
      });
    }
  }

  customElements.define('sticky-buy-bar', StickyBuyBar);
})();
